import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, GameStateGrant, GsAccessLevel, GsGrantReviewStatus, GsResourceType } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface UseAdminGrantsOptions {
  archeId?: string;
  resourceType?: GsResourceType;
  reviewStatus?: GsGrantReviewStatus;
  limit?: number;
  offset?: number;
}

export interface UseAdminGrantsReturn {
  grants: GameStateGrant[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAdminGrants(options?: UseAdminGrantsOptions): UseAdminGrantsReturn {
  const filters = {
    archeId: options?.archeId,
    resourceType: options?.resourceType,
    reviewStatus: options?.reviewStatus,
    limit: options?.limit,
    offset: options?.offset,
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.gameState.grantsList(filters),
    queryFn: () => api.adminListGameStateGrants(filters),
  });

  return {
    grants: data?.grants ?? [],
    total: data?.total ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load grants' : null,
    refresh: () => {
      refetch();
    },
  };
}

export function useAdminGrant(archeId: string, resourceType: GsResourceType, resourceName: string) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.gameState.grantDetail(archeId, resourceType, resourceName),
    queryFn: () => api.adminGetGameStateGrant(archeId, resourceType, resourceName),
    enabled: !!archeId && !!resourceType && !!resourceName,
  });
  return {
    grant: data?.grant ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load grant' : null,
    refresh: () => {
      refetch();
    },
  };
}

interface ApproveRejectInput {
  archeId: string;
  resourceType: GsResourceType;
  resourceName: string;
}

interface RejectInput extends ApproveRejectInput {
  reason?: string;
}

interface UpsertInput extends ApproveRejectInput {
  access: GsAccessLevel;
}

/**
 * Invalidate all grant-scoped query keys after a mutation. Includes the
 * grants-list keys (filter-keyed), the grant detail keys, and the parent
 * `grants()` namespace as a belt-and-braces fallback.
 */
function invalidateGrantQueries(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: queryKeys.gameState.grants() });
}

export function useApproveGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ archeId, resourceType, resourceName }: ApproveRejectInput) =>
      api.adminApproveGameStateGrant(archeId, resourceType, resourceName),
    onSuccess: () => invalidateGrantQueries(qc),
  });
}

export function useRejectGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ archeId, resourceType, resourceName, reason }: RejectInput) =>
      api.adminRejectGameStateGrant(archeId, resourceType, resourceName, reason),
    onSuccess: () => invalidateGrantQueries(qc),
  });
}

export function useUpsertGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ archeId, resourceType, resourceName, access }: UpsertInput) =>
      api.adminUpsertGameStateGrant(archeId, resourceType, resourceName, access),
    onSuccess: () => invalidateGrantQueries(qc),
  });
}

export function useDeleteGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ archeId, resourceType, resourceName }: ApproveRejectInput) =>
      api.adminDeleteGameStateGrant(archeId, resourceType, resourceName),
    onSuccess: () => invalidateGrantQueries(qc),
  });
}
